import {
  BdSharePermission,
  ShareableLabel,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import type { InlineNotificationSeverity } from '@bindoc-projects/ui-lib/feedback/ui';

export interface ShareableTypeOption {
  type: ShareableType;
  displayName: string;
}

/**
 * A persistent state shown where a result is expected — a folder that could not
 * be loaded, or a scope the user may not open. Rendered by the shared
 * `bd-ui-inline-notification` (styleguide "Notifications & messages" → inline),
 * so the severity/title/explanation split is the one the styleguide prescribes
 * instead of a hand-rolled coloured line of text.
 */
export interface InlineNotice {
  severity: InlineNotificationSeverity;
  /** Concise statement, no trailing punctuation. */
  title: string;
  /** Plain-language explanation — never a status code or internal id. */
  message?: string;
}

export interface FolderItem {
  id: string;
  type: ShareableType;
  isFolder: boolean;
  name: string;
  typeLabel: string;
  modifiedDate?: string;
  permissionSummary?: string;
  isRecycleBinItem?: boolean;
  parentFolderId?: string;
  labels?: ShareableLabel[];
  allowedPermissions?: BdSharePermission[];
}

/**
 * Row identity across reloads. The API hands out fresh objects on every fetch,
 * so a selection that must survive a refetch or a folder change (the resource
 * picker) has to be matched by `type` + `id`, never by object reference.
 */
export function isSameFolderItem(
  a: Pick<FolderItem, 'id' | 'type'>,
  b: Pick<FolderItem, 'id' | 'type'>,
): boolean {
  return a.id === b.id && a.type === b.type;
}

/** {@link isSameFolderItem} as a set/map key. */
export function folderItemKey(item: Pick<FolderItem, 'id' | 'type'>): string {
  return `${item.type}:${item.id}`;
}

/**
 * The flags of the folder table that together make up a *mode*: browsing a list
 * versus picking resources out of it. They are grouped because they only ever
 * change as a set — a picker needs all of them, a browser none — and handing
 * them down individually through two component levels is what made the resource
 * picker repeat the same block of bindings for every scope it shows.
 *
 * Flags that vary *within* a mode stay individual inputs: `showType` (the
 * workspace shows it whenever more than one type is listed, in either mode),
 * `sortMode`, `fillHeight`, `pageSize`, `showParentRow`, `highlightedId`.
 */
export interface FolderTablePresentation {
  /** Whether the leading checkbox column is rendered at all. */
  readonly showCheckbox: boolean;
  /**
   * Whether folder rows take part in the selection. `false` drops their
   * checkbox, keeps them out of the header select-all and out of every emitted
   * selection — in a picker, folders are navigation, not pickable resources.
   */
  readonly folderCheckbox: boolean;
  /** Whether the permissions column (and its narrow-layout glyphs) is shown. */
  readonly showPermissions: boolean;
}

/**
 * {@link FolderTablePresentation} plus the flags owned by the tab *around* the
 * table, so one binding configures a whole list surface.
 */
export interface ShareableListPresentation extends FolderTablePresentation {
  /** `false` hides every row-action menu and the management action bar. */
  readonly showActions: boolean;
  /** Whether the label filter above the table is offered (recent tab only). */
  readonly showLabelFilter: boolean;
}

/** Browsing a list: no checkbox column, full row actions and permissions. */
export const SHAREABLE_LIST_BROWSE: ShareableListPresentation = {
  showCheckbox: false,
  folderCheckbox: true,
  showPermissions: true,
  showActions: true,
  showLabelFilter: true,
};

/**
 * Picking resources out of a list: checkboxes on files only, and none of the
 * managing chrome — the embedding dialog owns the confirm action, and rights on
 * a resource are not what you choose it by.
 */
export const SHAREABLE_LIST_PICK: ShareableListPresentation = {
  showCheckbox: true,
  folderCheckbox: false,
  showPermissions: false,
  showActions: false,
  showLabelFilter: false,
};

export interface BreadcrumbEntry {
  id: string;
  name: string;
}

export type ScopeKind = 'user' | 'group';

/**
 * The two lists of a group scope: its folder tree ("Ordner & Dateien") and
 * what was shared with it ("Geteilt"). In the URL as `?view=shared`; the tree
 * is the default and has no param.
 */
export type GroupView = 'folders' | 'shared';

export interface NavigationScope {
  kind: ScopeKind;
  groupId?: string;
  groupName?: string;
  companyName?: string;
}

export type ViewTab =
  'recent' | 'shareables' | 'grants' | 'recycle-bin' | 'group';

/** Emitted by the scope sidebar when the user picks a destination. */
export interface SidebarNavigation {
  tab: ViewTab;
  groupId?: string;
  groupName?: string;
}

export interface ItemNavigateEvent {
  id: string;
  type: ShareableType;
  name: string;
}
